import { Injectable, Component } from '@angular/core';
import { OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';

import { Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { Geolocation } from '@awesome-cordova-plugins/geolocation/ngx';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';

import { languageSupport, languageService } from './language';
import { platformService } from './platform';
import { httpService } from './http';
import { alertService } from './alert';
import { toastService } from './toasts';
import { viewService } from './view';

import { AppConstants} from '@unpispas/upp-defs';

/**
 * Represents structured to store coordinates.
 */
export interface Coordinates {
    lat: number,
    lng: number
}

/**
 * Represents structured position data retrieved from the device.
 */
export interface WatchPosition {
    granted: boolean,
    coordinates: {
        lat: number,
        lng: number,
    },
    accuracy: number    
}

export interface GeocodePosition {
    coords: {
        latitude: number;
        longitude: number;
        altitude: number | null;
        accuracy: number;
        altitudeAccuracy: number | null;
        heading: number | null;
        speed: number | null;
    };
    timestamp: number;
}

export type GeocodePositionCallback = (position: GeocodePosition) => void
export type GeocodePositionErrorCallback = (error: GeolocationPositionError) => void;

/************************************/
/* GOOGLE ADDRESS DATA              */
/************************************/

/**
 * Represents structured address data retrieved from the Google Geocoding API.
 */
export interface Address {
    formatted: string;
    location: {
        lat : number,
        lng : number
    };
    number: string;
    street: {
        route: string,
        locality: string,
    };
    postal_code: string;
    area: {
        level1: string,
        level2: string,
        level3: string  // ISO 3166-1 alpha-2 country code
    },
    timezone: string;
}

/************************************/
/* GEOCODE SERVICE                  */
/************************************/

/**
 * Provides geocoding and geolocation services for interacting with Google Maps API
 * to retrieve address, timezone, and positional data.
 */

@Injectable({
    providedIn: 'root'
})
export class geocodeService {
    private _mapsurl = 'https://maps.googleapis.com/maps/api/geocode/json?key=' + AppConstants.googleApiKey;
    private _tmznurl = 'https://maps.googleapis.com/maps/api/timezone/json?key=' + AppConstants.googleApiKey;

    private _requestCache = new Map();
    private _resolveCache = new Map();
    private _timezneCache = new Map();

    private _requestAndroidPermissions() {
        this.androidPermissions.requestPermission(this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION);
        this.androidPermissions.requestPermission(this.androidPermissions.PERMISSION.ACCESS_COARSE_LOCATION);
    }
    
    public GMAP_API_LOADED = false;

    /**
     * Loads the Google Maps JS API and notifies `loaded` with success (`true`) or failure (`false`).
     * Always resolves so callers (including component constructors) never get an unhandled rejection.
     */
    LoadGoogleMaps(loaded: Subject<boolean>): Promise<void>  {
        return new Promise((resolve) => {
            const _Notify = (ok: boolean) => {
                setTimeout(() => {
                    loaded.next(ok);
                }, 0);
                resolve();
            };

            if ((window as any).google && (window as any).google.maps) {
                this.GMAP_API_LOADED = true;
                _Notify(true);
                return;
            }

            const _script = document.createElement('script');
            _script.async = true;
            _script.defer = true;
            _script.src = "https://maps.googleapis.com/maps/api/js?key=" + AppConstants.googleApiKey + "&loading=async&callback=initGoogleMaps";

            _script.onload = () => {
                console.info("[GOOGLE MAPS] API requested..");

                (window as any).initGoogleMaps = async () => {
                    this.GMAP_API_LOADED = true;
                    console.info("[GOOGLE MAPS] API loaded.");
                    _Notify(true);
                };
            };

            _script.onerror = (error) => {
                console.error("[GOOGLE MAPS] Error loading API: ", error);
                this.GMAP_API_LOADED = false;
                _Notify(false);
            };

            document.body.appendChild(_script);
        });
    }

    /**
     * Initializes the service and requests Android permissions if applicable.
     */    
    constructor(private androidPermissions: AndroidPermissions, private geolocation: Geolocation, private http: httpService, private lang: languageService, private modalCtrl: ModalController, private alertCtrl: alertService, private toast: toastService, private platform: platformService) { 
        this._requestAndroidPermissions();  // request location permission for android devices
    }
    
    // https://developers.google.com/maps/documentation/geocoding/start?hl=es
    private _parseGoogleResponse(response: any) {
        const address: Address = {
            formatted: '',
            location: {
                lat: 0.0,
                lng: 0.0
            },
            number: '',
            street: {
                route: '',
                locality: ''
            },
            postal_code: '',
            area: {
                level1: '',
                level2: '',
                level3: '',
            },
            timezone: 'UTC'
        };
        
        // https://developers.google.com/maps/documentation/geocoding/intro?hl=es#GeocodingResponses
        if ((response.status != 'OK') || (response.results.length == 0)) {
            console.error("[GEOCODE] Parsing google response", response);
            if (response.status != 'ZERO_RESULTS'){
                this.toast.ShowAlert('danger', this.lang.tr('@gmaps_request_error', [ response.status, response.error_message ]));
            }
            return null;
        }
        
        const data = response['results'][0];
        for(let i=0; i < data['address_components'].length; i++) {
            if (data.address_components[i]['types'].indexOf('street_number') != -1) {
                address.number = data.address_components[i]['long_name'];
            }
            
            if (data.address_components[i]['types'].indexOf('route') != -1) {
                address.street.route = data.address_components[i]['long_name'];
            }

            if (data.address_components[i]['types'].indexOf('locality') != -1) {
                address.street.locality = data.address_components[i]['long_name'];
            }
            
            if (data.address_components[i]['types'].indexOf('postal_code') != -1) {
                address.postal_code = data.address_components[i]['long_name'];
            }

            if (data.address_components[i]['types'].indexOf('administrative_area_level_1') != -1) {
                address.area.level1 = data.address_components[i]['long_name'];
            }
            
            if (data.address_components[i]['types'].indexOf('administrative_area_level_2') != -1) {
                address.area.level2 = data.address_components[i]['long_name'];
            }

            if (data.address_components[i]['types'].indexOf('country') != -1) {
                address.area.level3 = data.address_components[i]['short_name'];
            }
        }
        
        address.formatted = data['formatted_address'];
        address.location = {
            lat: parseFloat(data['geometry']['location']['lat']),
            lng: parseFloat(data['geometry']['location']['lng'])
        };
        
        return address;
    }
    
    /**
     * Generates a static map URL for a specific latitude and longitude.
     * 
     * @param lat - Latitude of the center point.
     * @param lng - Longitude of the center point.
     * @param width - Width of the generated map image.
     * @param height - Height of the generated map image.
     * @param zoom - Zoom level (default is 15).
     * @returns The constructed URL for the static map image.
     */    
    StaticUrl(lat: number, lng: number, width: number, height: number, zoom=15) {
        let _url = 'https://maps.googleapis.com/maps/api/staticmap';
        _url += '?key=' + AppConstants.googleApiKey;
        _url += '&size=' + width + "x" + height; 
        _url += '&center=' + lat + "," + lng + '&zoom=' + zoom;
        _url += '&markers=color:red|'+ lat + ',' + lng;
        return _url;
    }

    /**
     * Requests address data from the Google Geocoding API based on latitude and longitude.
     * Caches responses to reduce repeated network requests.
     * 
     * @param lat - Latitude of the requested location.
     * @param lng - Longitude of the requested location.
     * @returns A promise resolving to address data or `null` if an error occurs.
     */    
    RequestAddress(lat: number, lng: number): Promise <any> {
        return new Promise <any> ((resolve) => {

            const latlng =  lat + ',' + lng;
            if (this._requestCache.has(latlng)) {
                setTimeout(() => {
                    resolve(this._requestCache.get(latlng));
                }, 10);
            }
            else {
                const _request = this.http.get(this._mapsurl + '&latlng=' + latlng, false);
                _request.pipe(take(1)).subscribe({
                    next: (data) => {
                        const _address = this._parseGoogleResponse(data)
                        if (_address) {
                            this._requestCache.set(latlng, _address);
                        }
                        
                        resolve(_address);    
                    },
                    error: (error) => {
                        if (error.status != 0){
                            this.toast.ShowAlert('danger', this.lang.tr('@http_request_error', [ error.status ]));
                        }
                        else {
                            console.error("[HTTP] status = 0");
                        }
                        
                        resolve(null);
                    },
                    complete: () => {
                        // nothing to do
                    }
                });
            }
        });
    }
    
    /**
     * Requests timezone information for a specific location using the Google Timezone API.
     * 
     * @param lat - Latitude of the requested location.
     * @param lng - Longitude of the requested location.
     * @returns A promise resolving to the timezone ID or 'UTC' if an error occurs.
     */    
    RequestTimezne(lat: number, lng: number): Promise <string> {
        return new Promise <string> ((resolve) => {
            const latlng = lat.toFixed(4) + ',' + lng.toFixed(4);
            if (this._timezneCache.has(latlng)) {
                setTimeout(() => {
                    resolve(this._timezneCache.get(latlng));
                }, 10);
            }
            else {
                const tstamp = Math.floor(new Date().getTime() / 1000);

                const _request = this.http.get(this._tmznurl + '&location=' + latlng + '&timestamp=' + tstamp, false);
                _request.pipe(take(1)).subscribe({
                    next: (data: any) => {
                        if (data && data['timeZoneId']) {
                            this._timezneCache.set(latlng, data['timeZoneId']);
                        }                    
                        resolve(data['timeZoneId']);    
                    },
                    error: (error) => {
                        if (error.status != 0){
                            this.toast.ShowAlert('danger', this.lang.tr('@http_request_error', [ error.status ]));
                        }
                        else {
                            console.error("[HTTP] status = 0");
                        }
                        
                        resolve('UTC');     // fallback to UTC on error    
                    },
                    complete: () => {
                        // nothing to do
                    }
                });
            }
        });        
    }

    /**
     * Resolves an address from a string query using the Google Geocoding API.
     * Caches responses to optimize repeated lookups.
     * 
     * @param address - The address query string.
     * @returns A promise resolving to parsed address data or `false` if an error occurs.
     */    
    ResolveAddress(address: string): Promise <Address | null> {
        return new Promise <Address | null> ((resolve) => {        
            if (this._resolveCache.has(address)) {
                setTimeout(() => {
                    resolve(this._resolveCache.get(address));
                }, 10);
            }
            else {
                const _request = this.http.get(this._mapsurl + '&address=' + address, false);
                _request.pipe(take(1)).subscribe({
                    next: (data) => {
                        const _address = this._parseGoogleResponse(data);
                        if (_address) {
                            this._resolveCache.set(address, _address);
                        }
                        resolve(_address);    
                    },
                    error: (error) => {
                        if (error.status != 0){
                            this.toast.ShowAlert('danger', this.lang.tr('@http_request_error', [ error.status ]));
                        }
                        else {
                            console.error("[HTTP] status = 0");
                        }
                        resolve(null);    
                    },
                    complete: () => {
                        // nothing to do
                    }
                });
            }
        });
    }
    
    /**
     * Checks if geolocation services are available on the platform.
     */    
    get CanGeolocate(){
        if (this.platform.is('cordova')){
            return true;
        }
        else {
            return navigator.geolocation;
        }
    }
    
    private _ToGeoposition(position: any): GeocodePosition {
        return {
            coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                altitude: position.coords.altitude !== undefined ? position.coords.altitude : null,
                accuracy: position.coords.accuracy,
                altitudeAccuracy: position.coords.altitudeAccuracy !== undefined ? position.coords.altitudeAccuracy : null,
                heading: position.coords.heading !== undefined ? position.coords.heading : null,
                speed: position.coords.speed !== undefined ? position.coords.speed : null
            },
            timestamp: position.timestamp
        };
    }

    /**
     * Retrieves the current geolocation position.
     * 
     * @param resolve - Callback function executed on successful location retrieval.
     * @param reject - Callback function executed on failed location retrieval.
     */    
    GetCurrentPosition(resolve: GeocodePositionCallback, reject: GeocodePositionErrorCallback) {
        if (this.platform.is('cordova')){
            this.geolocation.getCurrentPosition()
            .then((resp) => {
                resolve(this._ToGeoposition(resp))
            })
            .catch((error) => {
                reject(error);
            });
        }
        else {
            const _options = {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            };    

            navigator.geolocation.getCurrentPosition(
                (position) => resolve(this._ToGeoposition(position)),
                (error: GeolocationPositionError) => reject(error),
                _options
            );
        }
    }

    /***************************/
    /* WATCH POSITION          */
    /***************************/

    private _positionChanged = new Subject<void> ();
    public OnPositionChanged = this._positionChanged.asObservable();

    private _watchpositionid: any | null = null;
    private _watchposition: WatchPosition = {
        granted: false,
        coordinates: {
            lat: 0,
            lng: 0,
        },
        accuracy: 0
    };

    /**
     * Returns the currently watched position
     */
    get CurrentPosition(): WatchPosition{
        return this._watchposition;
    }

    /**
     * Calculates the distance in meters between the current position and specified coordinates.
     * 
     * @param coordinates - Target coordinates.
     * @returns Distance in meters.
     */
    DistanceTo(coordinates: Coordinates): number {
        // https://stackoverflow.com/questions/365826/calculate-distance-between-2-gps-coordinates
        const _earthRadius = 6371;

        function degreesToRadians(degrees: number) {
          return degrees * Math.PI / 180;
        }
  
        const _dLat = degreesToRadians(this.CurrentPosition.coordinates.lat - coordinates.lat);
        const _dLon = degreesToRadians(this.CurrentPosition.coordinates.lng - coordinates.lng);

        const _lat1 = degreesToRadians(coordinates.lat);
        const _lat2 = degreesToRadians(this.CurrentPosition.coordinates.lat);

        const _a = Math.sin(_dLat/2) * Math.sin(_dLat/2) + Math.sin(_dLon/2) * Math.sin(_dLon/2) * Math.cos(_lat1) * Math.cos(_lat2);
        const _c = 2 * Math.atan2(Math.sqrt(_a), Math.sqrt(1 - _a));

        return Math.abs(Math.round(_earthRadius * _c * 1000));
    }

    /**
     * Starts watching the current geolocation position.
     * Updates the `_watchposition` property and notifies observers on position change.
     * 
     * @returns A promise resolving to `true` if watch started successfully, `false` otherwise.
     */    
    private StartWatch(){
        return new Promise <boolean> ((resolve) => {
            if (this._watchpositionid){
                resolve(true);
                this._positionChanged.next();   // already started
            }
            else {
                if (this.platform.is('cordova')){
                    this._watchpositionid = this.geolocation.watchPosition().subscribe(
                    data => {
                        if ('coords' in data) {
                            const _nextposition: WatchPosition = {
                                granted: true,
                                coordinates: {
                                    lat: data.coords.latitude,
                                    lng: data.coords.longitude,
                                },
                                accuracy: data.coords.accuracy,
                            };
            
                            if (JSON.stringify(this._watchposition) !== JSON.stringify(_nextposition)) {
                                this._watchposition = _nextposition;

                                resolve(true);
                                this._positionChanged.next();
                            }
                        } 
                        else {
                            console.error("[GEOLOCATION] error in watchPosition: ", data.message);
                            resolve(false);
                        }
                    }, 
                    (err) => {
                        this._watchpositionid = null;
                        console.error("[GEOLOCATION] error on watchposition: ", err.message);
                        switch (err.code) {
                            case 1:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_denied'));
                                break;
                            case 2:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_unavailable'));
                                break;
                            case 3:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_expired'));
                                break;
                            default:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_error', [err.message, err.code.toString()]));
                                break;
                        }
                        resolve(false);
                    });
                }
                else {
                    const _options: PositionOptions = {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: 0
                    };    
        
                    this._watchpositionid = navigator.geolocation.watchPosition(
                    (pos) => {
                        const _nextposition: WatchPosition = {
                            granted: true,
                            coordinates: {
                                lat: pos.coords.latitude,
                                lng: pos.coords.longitude,
                            },
                            accuracy: pos.coords.accuracy
                        }

                        if (JSON.stringify(this._watchposition) != JSON.stringify(_nextposition)){
                            this._watchposition = _nextposition;

                            resolve(true);
                            this._positionChanged.next();
                        }
                    }, (err) => {
                        this._watchpositionid = null;
                        
                        console.error("[GEOLOCATION] error on watchposition: ", err.message);
                        switch(err.code){
                            case 1:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_denied'));
                                break;
                            case 2:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_unavailable'));
                                break;
                            case 3:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_expired'));
                                break;
    
                            default:
                                this.toast.ShowAlert('danger', this.lang.tr('@geolocation_error', [ err.message, err.code.toString() ]))
                                break;
                        }
                        
                        resolve(false);
                    }, _options);
                }    
            }    
        });
    }

    /**
     * Stops watching the geolocation position.
     * Resets the `_watchposition` property and clears the watch ID.
     */    
    private ClearWatch(){
        if (this._watchpositionid){
            if (this.platform.is('cordova')){
                this._watchpositionid.unsubscribe();
            }
            else {
                navigator.geolocation.clearWatch(this._watchpositionid);
            }

            this._watchposition = {
                granted: this._watchposition.granted,
                coordinates: {
                    lat: 0,
                    lng: 0
                },
                accuracy: 0
            };

            this._watchpositionid = null;
        }
    }
  
    private _nearby_farawaymodal = false;        // keep control of away modal being displayed

    private async ShowAway(center: Coordinates){
        const modal = await this.modalCtrl.create({ 
            component: modalAwayComponent,
            backdropDismiss: false,
            cssClass: 'modal-away',
            componentProps: { 
                center: center,
            }
        });

        if (this._nearby_farawaymodal){
            await modal.present();        

            const detail = await modal.onDidDismiss();
            this._nearby_farawaymodal = false;
            return detail?.data || null;
        }

        return null;
    }
    
    private async AskPermission(){
        const alert = await this.alertCtrl.alert({
            header: this.lang.tr('@require_geolocate_title'),
            message: this.lang.tr('@require_geolocate_message'),
            buttons: [{
                text: this.lang.tr('@ok'),
                handler: () => {
                    alert.dismiss(true);
                }
            }]
        });

        await alert.present();            

        await alert.onDidDismiss();
        await new Promise(resolve => setTimeout(resolve, 100));
        
        return await this.StartWatch();
    }

    private IsNear(center: Coordinates, radius: number){
        return (this.DistanceTo(center) <= radius + this.CurrentPosition.accuracy)
    }

    /**
     * Waits until the current position is within a specified radius of the center coordinates.
     * Shows a modal if the user is outside the radius, and waits until they are nearby.
     * 
     * @param center - The center coordinates.
     * @param radius - Radius in meters (default is 250).
     * @returns A promise resolving to `true` if within radius, `false` otherwise.
     */    
    WaitForNear(center: Coordinates, radius = 250) : Promise <boolean> {
        return new Promise  <boolean> ((resolve) => {
            if (this.CurrentPosition){
                if (this.IsNear(center, radius)){
                    resolve(true);
                }
                else {
                    if (!this._nearby_farawaymodal){
                        this._nearby_farawaymodal = true;
                        this.ShowAway(center).then(
                        data => { 
                            this._nearby_farawaymodal = false;
                            resolve(data ? true : false);
                        });
                    }
                }
            }
            else {
                this.OnPositionChanged.pipe(take(1)).subscribe(
                () => {
                    resolve(this.WaitForNear(center, radius));
                });             
            }
        });
    }

    /**
     * Enables or disables a proximity-based restriction.
     * Starts watching the position if required and checks proximity.
     * 
     * @param center - The center coordinates or `null` to disable the restriction.
     * @param radius - Radius in meters (default is 250).
     * @returns A promise resolving to `true` if within radius or restriction is disabled, `false` otherwise.
     */    
    async NearBy(center: Coordinates | null, radius = 250) : Promise <boolean> {
        // use center = null to disable requirement
        if (center == null){
            console.info("[GEOLOCATE] disabling near-by restrinction")
            
            this.ClearWatch();

            if (this._nearby_farawaymodal){
                this._nearby_farawaymodal = false;
            }

            return true;    // disabled
        }
        else {
            if (!this._watchpositionid){     // we must start watching
                console.info("[GEOLOCATE] near-by restrinction enabled to " + radius + " metres");

                let _granted = this._watchposition.granted;
                if (!_granted){
                    _granted = await this.AskPermission();
                }
                else {
                    _granted = await this.StartWatch();
                }

                if (!_granted){     // permission not granted
                    return false;
                }
            }

            if (this._watchpositionid){
                return (await this.WaitForNear(center, radius)) ? true : false;
            }
        }

        return false;   // could not start watching
    }
}

/************************************/
/* MODAL FAR AWAY                   */
/************************************/

/**
 * A modal component that displays information when the user is far from a specified center location.
 * It includes distance monitoring and an option to close the modal when the user is within range.
 */
@Component({
    selector: 'upp-modal-away',
    templateUrl: './away/modal/modal-away.html',
    styleUrls: [
        './away/modal/modal-away.scss'
    ]    
})
export class modalAwayComponent {

    /**
     * The center coordinates that the distance is measured from.
     */
    @Input() center: Coordinates | null = null;

    /**
     * Initializes the component and retrieves the center coordinates from modal parameters.
     * 
     * @param modalCtrl - Controller for managing modal dismissal.
     */
    constructor(private modalCtrl: ModalController) {
        // nothing to do
    }

    private inrange = 250;

    /**
     * Handles changes to the user's distance from the center.
     * Closes the modal if the distance is within the specified range plus the accuracy.
     * 
     * @param data - Object containing `distance` and `accuracy` properties for the user's current position.
     */    
    OnChange(data: any){
        if (data.distance < (this.inrange + data.accuracy)){
            (document.activeElement as HTMLElement)?.blur();
            this.modalCtrl.dismiss(true);
        }
    }

    /**
     * Manually closes the modal without confirming proximity.
     */
    OnClose(){
        (document.activeElement as HTMLElement)?.blur();
        this.modalCtrl.dismiss(false);
    }
}

/************************************/
/* FAR AWAY INFORMATION             */
/************************************/

/**
 * A component that displays information when the user is far from a specified center location.
 * Includes map display and location information based on Google Maps API.
 */
@Component({
    selector: 'upp-away',
    templateUrl:'./away/view/ui-away.html',
    styleUrls: [ 
        './away/view/ui-away.scss'
    ]
})
export class UiAwayComponent extends languageSupport implements OnInit, AfterViewInit, OnDestroy {
    /**
     * The center coordinates to calculate the distance from.
     */    
    @Input() _center: Coordinates | null = null;
    
    private _gmapsApiLoaded = new Subject <boolean> ();
    public gmapsApiLoaded = this._gmapsApiLoaded.asObservable();

    /**
     * Map display information, including markers, accuracy, and zoom level.
     */    
    public _mapinfo = {
        zoom: 17,
        accuracy: 0,
        position: {
            lat: 0, 
            lng: 0
        },
        markers: [{
            position: {
                lat: 0,
                lng: 0
            },
            options: {
                draggable: false,
                icon: '/assets/image/marker-place.png'
            }
        }, {
            position: {
                lat: 0,
                lng: 0
            },
            options: {
                draggable: false
            }
        }]
    };

    private _distance = 0;

    /**
     * Gets the distance to the center point. Displays in kilometers if the distance is over 1,000 meters.
     */    
    get Distance(){
        if (this._distance > 1000){
            return (this._distance / 1000).toFixed(2) + " " + this.lang.tr("@kilometres");
        }
        else {
            return this._distance + " " + this.lang.tr("@metres");
        }
    }

    /**
     * Loads the Google Maps API script if not already loaded and initializes language and location services.
     */    
    constructor(private lang: languageService, private geocode: geocodeService, public view: viewService, private http: HttpClient) {
        super(lang, null);

        // https://github.com/angular/components/tree/master/src/google-maps
        if (!geocode.GMAP_API_LOADED) {
            this.geocode.LoadGoogleMaps(this._gmapsApiLoaded);
        }
        else {
            console.warn("[GOOGLE MAPS] API already loaded");
            setTimeout(() => {
                this._gmapsApiLoaded.next(true);
            }, 0);
        }
    }
    
    // https://stackoverflow.com/questions/6048975/google-maps-v3-how-to-calculate-the-zoom-level-for-a-given-bounds    
    @ViewChild('googlemap', { static: false }) _googlemap: any;                                      
    private BoundsZoomLevel(coords1: Coordinates, coords2: Coordinates) {
        const WORLD_DIM = { height: 256, width: 256 };
        const ZOOM_MAX = 21, ZOOM_DEFAULT = 17;

        function getNorthEast(coords1: Coordinates, coords2: Coordinates){
            return { 
                lat: (coords1.lat > coords2.lat) ? coords1.lat : coords2.lat, 
                lng: (coords1.lng > coords2.lng) ? coords1.lng : coords2.lng
            };
        }

        function getSouthWest(coords1: Coordinates, coords2: Coordinates){
            return { 
                lat: (coords1.lat < coords2.lat) ? coords1.lat : coords2.lat, 
                lng: (coords1.lng < coords2.lng) ? coords1.lng : coords2.lng 
            };            
        }
        
        function latRad(lat: number) {
            const sin = Math.sin(lat * Math.PI / 180);
            const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
            return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
        }

        function zoom(mapPx: number, worldPx: number, fraction: number) {
            return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
        }
                                      
        let mapDim = { height: 0, width: 0 };
        if (!this._googlemap){
            return ZOOM_DEFAULT;
        }
        else {
            mapDim = {
                height: this._googlemap._elementRef.nativeElement.clientHeight,
                width: this._googlemap._elementRef.nativeElement.clientHeight
            };
            
            if ((mapDim.height == 0) || (mapDim.width == 0)){
                return ZOOM_DEFAULT;
            }
        }

        const ne = getNorthEast(coords1, coords2);
        const sw = getSouthWest(coords1, coords2);

        const latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;

        const lngDiff = ne.lng - sw.lng;
        const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

        const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
        const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

        return Math.min(latZoom, lngZoom, ZOOM_MAX) - 2;
    }

    private _locate_subscription: Subscription | null = null;

    /**
     * Subscribes to location changes to update the position on initialization.
     */    
    ngOnInit(){
        this._locate_subscription = this.geocode.OnPositionChanged.subscribe(
        () => {
            this.UpdatePosition();
        });
    }
    
    /**
     * Updates the map position after the view initializes.
     */    
    ngAfterViewInit(){
        this.UpdatePosition();
    }

    /**
     * Unsubscribes from location updates on component destruction.
     */    
    ngOnDestroy(){
        super.OnDestroy();

        if (this._locate_subscription){
            this._locate_subscription.unsubscribe();
            this._locate_subscription = null;
        }
    }

    /**
     * Emits an event when the distance or accuracy changes.
     */
    @Output() changeEvent = new EventEmitter<any>();    
    
    private UpdatePosition(){
        if (!this._googlemap){
            setTimeout(() => {
                this.UpdatePosition()
            }, 250);
        }
        else {
            let _center: Coordinates = {
                lat: 0,
                lng: 0
            };

            if (this._center){
                _center = this._center;
            }

            this._distance = this.geocode.DistanceTo(_center);

            this._mapinfo.markers[0].position = {
                lat: this._center?.lat || 0,
                lng: this._center?.lng || 0
            }
    
            this._mapinfo.markers[1].position = {
                lat: this.geocode.CurrentPosition.coordinates.lat,
                lng: this.geocode.CurrentPosition.coordinates.lng,
            }
    
            this._mapinfo.position = {
                lng: (this._mapinfo.markers[0].position.lng + this._mapinfo.markers[1].position.lng) / 2,
                lat: (this._mapinfo.markers[0].position.lat + this._mapinfo.markers[1].position.lat) / 2,
            }
    
            this._mapinfo.zoom = this.BoundsZoomLevel(
                this._mapinfo.markers[0].position, 
                this._mapinfo.markers[1].position
            );
    
            this.changeEvent.emit({
                accuracy: this.geocode.CurrentPosition.accuracy,
                distance: this._distance
            });    
        }
    }

     /**
     * Event emitted when the component is closed.
     */
    @Output() closeEvent = new EventEmitter<any>();    

    /**
     * Emits the `closeEvent` to notify listeners that the component should close.
     */
    OnClose(){
        this.closeEvent.emit();
    }
}

